import type { EventEmitter2 } from "eventemitter2";

import AnalyticsClient from "@framework/analytics/analytics-client";

/**
 * Mapping of events in Coral to events used in analytics. Sourced from SBN.
 *
 * @see https://github.com/voxmedia/sbn/blob/2beca5f1a7d39334c5e853f7ed5d6fe57a9ad4e3/app_unison/frontend/stable/components/apps/coral/index.js#L198-L210
 * @see https://github.com/coralproject/talk/blob/develop/CLIENT_EVENTS.md
 */
const mapping: Record<string, string> = {
  "createComment.success": "comment",
  "createCommentReply.success": "comment",
  "editComment.success": "edit",
  "reportComment.success": "report",
  "createCommentReaction.success": "respect",
  showSharePopover: "share",
  setCommentsOrderBy: "set_sort_order",
  unmarkAll: "unmark_unread",
  jumpToNextUnseenComment: "z_key_next",
  setProfileTab: "set_profile_tab",
  "ignoreUser.success": "ignore_user",
  copyPermalink: "copy_permalink",
};

/**
 * CoralEvents is an event handler for events emitted by the Coral embed. These
 * events are mapped to both analytics events as well as other UI interactions.
 */
export default class CoralEvents {
  private events: EventEmitter2 | null = null;

  constructor(
    private readonly client: AnalyticsClient,
    private readonly ssoConfig?: {
      ssoAuthDomain: string;
      isPrimaryCommunity: boolean;
    },
  ) {}

  /*
   * The user has clicked an action or button that requires login, send
   * them to the login screen.
   */
  private handleOnLoginPrompt = () => {
    this.client.emit({
      event: "authentication",
      authentication: { method: "sign in and join the conversation" },
    });

    const authLink = this.getIdentityAuthLink();

    window.location.href = authLink;
  };

  /**
   * If the community is part of an SSO network but not the primary community,
   * link to the SSO auth host for login with a returnTo param back to the current URL.
   * Otherwise, link to the normal login/signup flow.
   */
  private getIdentityAuthLink = () => {
    const { protocol, href, pathname, search } = window.location;

    if (this.ssoConfig?.ssoAuthDomain && !this.ssoConfig.isPrimaryCommunity) {
      return `${protocol}//${this.ssoConfig.ssoAuthDomain}/auth/sso?returnTo=${encodeURIComponent(href)}`;
    }

    const returnPath = encodeURIComponent(pathname + search + "#comments");
    return `/auth/login?returnPath=${returnPath}`;
  };

  /**
   * Handles when any event occurs and tries to map it to an analytics event.
   */
  private handleOnAny = (eventName: string | string[]) => {
    if (Array.isArray(eventName)) {
      return;
    }

    const action = mapping[eventName];
    if (!action) {
      return;
    }

    this.client.emit({
      event: "coral_comment",
      coral_comment: { action },
    });
  };

  public connect(events: EventEmitter2) {
    // If there is events attached already, detach them now.
    if (this.events) {
      this.disconnect();
    }

    this.events = events;
    this.events.on("loginPrompt", this.handleOnLoginPrompt);
    this.events.onAny(this.handleOnAny);
  }

  public disconnect() {
    // If there was no events attached already, we're done!
    if (!this.events) {
      return;
    }

    this.events.off("loginPrompt", this.handleOnLoginPrompt);
    this.events.offAny(this.handleOnAny);
    this.events = null;
  }
}
